if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('.sticky-atc__button');
        this.priceEl = this.querySelector('.sticky-atc__price');
        this.imageEl = this.querySelector('.sticky-atc__image');
        this.buttonPriceEl = this.querySelector('.sticky-atc__button-price');
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainPriceEl = document.getElementById(`price-${this.sectionId}`);

        if (!this.mainButton) return;

        this.button?.addEventListener('click', () => {
          if (this.mainButton.hasAttribute('disabled')) return;
          // Tell the shared fly-to-cart animation (assets/custom.js) to
          // start from this button instead of the product image, since the
          // image is likely off-screen once this bar is visible.
          window.__flyToCartOrigin = this.button;
          this.mainButton.click();
        });

        // Show only once the user has scrolled the real Add to Cart button
        // fully above the viewport — rAF-throttled to avoid a forced reflow
        // on every scroll event.
        this.ticking = false;
        this.boundUpdate = this.updateVisibility.bind(this);
        window.addEventListener('scroll', () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.boundUpdate();
            this.ticking = false;
          });
        }, { passive: true });
        requestAnimationFrame(this.boundUpdate);

        this.subscribeToVariantChange();

        // Keep price in sync with whichever Appstle purchase option
        // (one-time vs. subscribe) is currently selected, not just the
        // underlying variant.
        this.syncPrice();
        document.addEventListener('change', (e) => {
          if (e.target.matches?.('input[name="selling_plan"]')) {
            this.syncPrice();
          }
        });

        // Appstle re-renders its own price display asynchronously — after
        // page load (to apply its real default selection) AND after a
        // size/grind change (to recalculate for the new variant) — on its
        // own timeline that we can't predict with a fixed delay (retries at
        // guessed intervals kept catching stale text one step behind).
        // Watching for the actual DOM mutation instead reacts the instant
        // Appstle's price text really changes, regardless of how long that
        // takes.
        // Appstle injects its widget asynchronously, so it may not exist yet
        // right here. Poll briefly for it (cheap, short-lived) and attach the
        // observer directly to it once found — scoped to just the widget so
        // syncPrice()'s own DOM writes (elsewhere on the page) can't
        // re-trigger this observer, unlike watching document.body would.
        this.attachPriceObserver();
      }

      attachPriceObserver(attempt = 0) {
        const widget = document.querySelector('.appstle_sub_widget');
        if (widget) {
          const priceObserver = new MutationObserver(() => this.syncPrice());
          priceObserver.observe(widget, {
            subtree: true,
            childList: true,
            characterData: true,
          });
          // The widget may not have Appstle's default selection applied the
          // instant it appears — sync once more now that it exists.
          this.syncPrice();
          return;
        }
        // Keep polling for up to ~10s in case Appstle's script is slow to
        // inject the widget; give up quietly after that.
        if (attempt < 40) {
          setTimeout(() => this.attachPriceObserver(attempt + 1), 250);
        }
      }

      // Looks at whichever Appstle purchase-option box currently has its
      // radio checked and reads its displayed price directly, so the
      // floating bar always matches one-time vs. subscribe pricing —
      // Appstle's own price isn't reflected in Dawn's price block at all.
      getActiveOptionPrice() {
        const activeWrapper = document.querySelector(
          '.appstle_subscription_wrapper_option:has(input:checked)'
        );
        if (!activeWrapper) return null;

        const currentEl = activeWrapper.querySelector('.appstle_subscription_amount');
        if (!currentEl) return null;

        // Only show the crossed-out original price when Subscribe & Save
        // is the active option — One Time Purchase shows just its own
        // price, no strikethrough, even if it also has a compare-at value.
        const isSubscribe = activeWrapper.classList.contains('appstle_include_dropdown');
        const compareEl = isSubscribe
          ? activeWrapper.querySelector(
              '.appstle_subscription_amount_compare_at_price, .appstle_subscription_compare_amount, .appstle_subscription_compare_at_price'
            )
          : null;

        return {
          current: currentEl.textContent.trim(),
          compare: compareEl ? compareEl.textContent.trim() : null,
        };
      }

      renderPrice(target, priceData) {
        if (!target) return;
        target.textContent = '';
        target.appendChild(document.createTextNode(priceData.current));
        if (priceData.compare && priceData.compare !== priceData.current) {
          target.appendChild(document.createTextNode(' '));
          const s = document.createElement('s');
          s.textContent = priceData.compare;
          target.appendChild(s);
        }
      }

      syncPrice() {
        const activePrice = this.getActiveOptionPrice();
        if (activePrice) {
          this.renderPrice(this.priceEl, activePrice);
          this.renderPrice(this.buttonPriceEl, activePrice);
          return;
        }

        // Fallback: no Appstle widget (or nothing selected yet) — use
        // Dawn's own price block for the current variant.
        if (!this.mainPriceEl) return;
        const saleWrap = this.mainPriceEl.querySelector('.price__sale');
        const regularWrap = this.mainPriceEl.querySelector('.price__regular');
        const visibleWrap =
          saleWrap && getComputedStyle(saleWrap).display !== 'none' ? saleWrap : regularWrap;
        const priceItem = visibleWrap?.querySelector('.price-item--sale, .price-item--regular');
        if (priceItem) {
          const priceText = priceItem.textContent.trim();
          if (this.priceEl) this.priceEl.textContent = priceText;
          if (this.buttonPriceEl) this.buttonPriceEl.textContent = priceText;
        }
      }

      updateVisibility() {
        const rect = this.mainButton.getBoundingClientRect();
        const scrolledPast = rect.bottom < 0;
        this.classList.toggle('sticky-atc--visible', scrolledPast);
      }

      subscribeToVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          const variant = event.data.variant;
          const label = this.button?.querySelector('.sticky-atc__button-label');

          // The real price block is already updated by product-info.js by
          // the time this event fires — syncPrice() picks whichever is
          // relevant (Appstle's active option, or Dawn's price block).
          // Appstle's own re-render of its price for the new variant is
          // caught separately by the MutationObserver set up in
          // connectedCallback, since its timing can't be predicted here.
          this.syncPrice();

          if (this.imageEl && variant && variant.featured_image && variant.featured_image.src) {
            const src = variant.featured_image.src;
            const separator = src.includes('?') ? '&' : '?';
            this.imageEl.src = `${src}${separator}width=100`;
          }

          if (!variant) {
            this.button?.setAttribute('disabled', 'disabled');
            if (label) label.textContent = window.variantStrings?.unavailable || label.textContent;
            return;
          }

          if (variant.available) {
            this.button?.removeAttribute('disabled');
            if (label) label.textContent = window.variantStrings?.addToCart || label.textContent;
          } else {
            this.button?.setAttribute('disabled', 'disabled');
            if (label) label.textContent = window.variantStrings?.soldOut || label.textContent;
          }
        });
      }
    }
  );
}
